/**
 * A voice agent's own call openings (`call_openings` in `agents/<slug>.yaml`, gh #4418) — the
 * push (local file → content hash + bytes) and pull (hash → local file + download) halves for
 * the CLI, the `eval-attachments.ts` split.
 *
 * ON DISK a language's opening is a hub-relative `file` and a `caption`:
 *
 *   call_openings:
 *     pt:
 *       file: call-openings/voice-pt.mp3
 *       caption: "Olá! Você está falando com a assistente virtual da Clínica Aurora."
 *
 * ON THE WIRE it is the bytes' `hash` and the caption, with the bytes in the payload-level
 * `call_opening_files` carrier, deduped by hash. The server checks the bytes (an MP3 or AAC
 * audio in MP4, bounded in size and length) before it writes anything. Pull writes each
 * opening to `call-openings/<agent-slug>-<language>.<ext>`.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import {
  CALL_OPENING_LANGUAGES,
  CALL_OPENING_MAX_BYTES,
  type HubAsCodeCallOpeningFile,
  type HubAsCodePayload,
} from '@wayai/core/contracts';
import { computeHash } from './resource-files.js';
import { ensureRealSubdirNoSymlink, isUnder, realpathContained, writeFileNoFollow } from './fs-safety.js';
import { expected } from './expected.js';
import { slugify } from './utils.js';

/** Where pull writes the openings, hub-root. */
export const CALL_OPENINGS_DIR = 'call-openings';

/**
 * Resolve every agent's on-disk opening `file`s to wire hashes, IN PLACE, and carry their
 * bytes in `payload.call_opening_files`. An opening already named by `hash` (a YAML written by
 * an older CLI) passes as it is: the server holds its bytes or refuses the push.
 *
 * Hard-errors (naming the agent and language) on a path escaping the hub folder or reached
 * through a symlink, a missing file, and one over the size bound: a dropped opening would
 * change what callers hear.
 */
export function applyCallOpeningFiles(hubFolder: string, payload: HubAsCodePayload): void {
  const root = path.resolve(hubFolder);
  const carriers = new Map<string, HubAsCodeCallOpeningFile>();
  for (const agent of payload.agents ?? []) {
    const openings = agent.call_openings;
    if (!openings || typeof openings !== 'object') continue;
    for (const [language, opening] of Object.entries(openings)) {
      if (!opening || typeof opening !== 'object' || typeof opening.file !== 'string') continue;
      const label = `Agent "${agent.name}" call_openings.${language}`;
      const abs = path.resolve(root, opening.file);
      if (!isUnder(root, abs)) {
        throw expected(`${label}: file "${opening.file}" escapes the hub folder.`);
      }
      let stat: fs.Stats | undefined;
      try {
        stat = fs.statSync(abs);
      } catch {
        /* falls through to not-found */
      }
      if (!stat?.isFile()) throw expected(`${label}: file "${opening.file}" not found.`);
      if (!realpathContained(root, abs)) {
        throw expected(`${label}: file "${opening.file}" resolves outside the hub folder (symlinks are not allowed).`);
      }
      if (stat.size > CALL_OPENING_MAX_BYTES) {
        throw expected(`${label}: file "${opening.file}" is ${(stat.size / 1024 / 1024).toFixed(1)} MB, over the ${CALL_OPENING_MAX_BYTES / 1024 / 1024} MB limit.`);
      }
      const bytes = fs.readFileSync(abs);
      const hash = computeHash(bytes);
      const { file: _file, ...rest } = opening;
      openings[language as keyof typeof openings] = { ...rest, hash };
      if (!carriers.has(hash)) carriers.set(hash, { hash, content_base64: bytes.toString('base64') });
    }
  }
  if (carriers.size > 0) payload.call_opening_files = [...carriers.values()];
}

/** One opening to fetch on pull: signed URL → hub-relative path. */
export interface CallOpeningDownload {
  relPath: string;
  url: string;
  hash: string;
}

/**
 * Rewrite every agent's wire openings (`hash`) to on-disk `file`s, IN PLACE, and return the
 * downloads. Runs before the YAML is written, which emits the agents' keys verbatim. The file
 * name is built from the agent's slug and the language, never from a server-supplied name.
 */
export function rewriteCallOpeningsToLocalPaths(payload: HubAsCodePayload): CallOpeningDownload[] {
  const byHash = new Map((payload.call_opening_files ?? []).map((file) => [file.hash, file]));
  const downloads: CallOpeningDownload[] = [];
  for (const agent of payload.agents ?? []) {
    const openings = agent.call_openings;
    if (!openings || typeof openings !== 'object') continue;
    for (const language of CALL_OPENING_LANGUAGES) {
      const opening = openings[language];
      if (!opening || typeof opening.hash !== 'string') continue;
      const carrier = byHash.get(opening.hash);
      const ext = carrier?.content_type === 'audio/mp4' ? '.m4a' : '.mp3';
      const relPath = `${CALL_OPENINGS_DIR}/${slugify(agent.name)}-${language}${ext}`;
      const { hash, ...rest } = opening;
      openings[language] = { ...rest, file: relPath };
      if (carrier?.download_url) downloads.push({ relPath, url: carrier.download_url, hash });
    }
  }
  // The byte carrier is a wire-only field: never written to disk.
  delete payload.call_opening_files;
  return downloads;
}

/**
 * Fetch the openings into the hub folder, then prune what the pull no longer names from
 * `call-openings/`. A file already holding the same bytes is not fetched again. The same
 * symlink guards as `downloadEvalAttachments`.
 */
export async function downloadCallOpenings(
  hubFolder: string,
  downloads: CallOpeningDownload[],
): Promise<{ changed: string[]; removed: string[] }> {
  const dir = path.resolve(hubFolder, CALL_OPENINGS_DIR);
  const result = { changed: [] as string[], removed: [] as string[] };
  if (!ensureRealSubdirNoSymlink(hubFolder, dir, downloads.length > 0)) {
    console.warn(`  Warning: ${CALL_OPENINGS_DIR}/ is not reachable inside the hub folder without a symlink; skipping call opening sync.`);
    return result;
  }
  if (!fs.existsSync(dir)) return result;

  for (const download of downloads) {
    const abs = path.resolve(hubFolder, download.relPath);
    if (abs === dir || !isUnder(dir, abs)) continue;
    let existing: fs.Stats | undefined;
    try { existing = fs.lstatSync(abs); } catch { /* absent */ }
    if (existing?.isFile() && !existing.isSymbolicLink() && computeHash(fs.readFileSync(abs)) === download.hash) continue;
    try {
      const res = await fetch(download.url);
      if (!res.ok) {
        console.warn(`  Warning: failed to download ${download.relPath} (HTTP ${res.status})`);
        continue;
      }
      if (writeFileNoFollow(hubFolder, abs, Buffer.from(await res.arrayBuffer()))) result.changed.push(download.relPath);
    } catch (err) {
      console.warn(`  Warning: failed to download ${download.relPath}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  const keep = new Set(downloads.map((download) => path.resolve(hubFolder, download.relPath)));
  for (const name of fs.readdirSync(dir)) {
    const abs = path.join(dir, name);
    const st = fs.lstatSync(abs);
    if ((st.isFile() || st.isSymbolicLink()) && !keep.has(abs)) {
      fs.rmSync(abs);
      result.removed.push(path.relative(hubFolder, abs));
    }
  }
  return result;
}
